import React from 'react'
import {get, isEmpty, isNil, uniqBy} from 'lodash'
import {DialogMessageType} from 'components/dialogMessage/dialogMessageType'
import ValidationErrorList from 'components/validationErrorList'
import {accountingTypes, permissions} from 'helpers/constants'
import {InvoiceType, Posted_Invoice_Status} from 'helpers/invoices'
import {userHasPermission} from 'helpers/user'
import {canEditPurchaseInvoice, isSupplierRefMandatory} from 'helpers/purchaseInvoices'
import {isAccountIntegrationEnabled} from 'helpers/integrations'
import {isEmailValid} from "helpers/logins"
import {parseCssSelector} from "helpers/css"

export const getAccountsIntegrationForUser = (user) => {
  const id = get(user, 'settings.accountsIntegration.id')
  return accountingTypes.forId(id)
}

export const asyncMarkInvoiceAsPosted = (state, action) => {
  return state && state.id === action.data
    ? {
      ...state,
      posted: true,
    }
    : state
}

export const unmarkInvoiceAsPosted = (state, action) => {
  return state && state.id === action.data
    ? {
      ...state,
      posted: false,
    }
    : state
}

export const unmarkPurchaseInvoiceAsPosted = (state, action) => {
  return state && state.id === action.data
    ? {
      ...state,
      posted: false,
    }
    : state
}

export const updateAccountingInvoiceId = (state, action) => {
  const invoices = Object.values(get(action, 'data.invoiceMap', {}))
  const invoiceId = get(state, 'id', null)
  const invoice = invoices.find(invoice => invoice.id === invoiceId)
  return !isNil(invoice)
    ? {
      ...state,
      accountingInvoiceId: invoice.accountingInvoiceId,
    }
    : state
}

export const getAccountingErrorLabels = (accountingErrors) => {
  return accountingErrors.map(error => error.label)
}

export const isPostToAccountingDisabled = (accountingErrors) => {
  const errorType = getAccountingErrorType(accountingErrors)
  return errorType === DialogMessageType.ERROR
}

export const getAccountingErrorType = (accountingErrors) => {
  const errorTypes = accountingErrors.map(error => error.type)

  if (errorTypes.includes(DialogMessageType.ERROR))
    return DialogMessageType.ERROR

  if (errorTypes.includes(DialogMessageType.WARNING))
    return DialogMessageType.WARNING

  return DialogMessageType.INFO
}

export const getAccountingLabel = (entity, isInvoice) => {
  const isCreditNote = get(entity, 'creditNote', false)
  return isInvoice
    ? isCreditNote ? 'credit note' : 'invoice'
    : isCreditNote ? 'purchase invoice credit note' : 'purchase invoice'
}

export const getAccountingErrors = (
  user,
  entity,
  items,
  isInvoice,
  accountsIntegration,
  requiredFields = {}
) => {

  const isAccountingPackageConnected = isAccountIntegrationEnabled(accountsIntegration)

  const accountingErrors = []

  const posted = get(entity, 'posted', false)
  const postStatus = get(entity, 'postStatus.value', '')
  const type = get(entity, 'type', InvoiceType.standard)
  const contactId = get(entity, 'contactId', null)
  const companyId = get(entity, 'companyId', null)
  const email = get(entity, 'contact.email', null)
  const currencyId = get(entity, 'currencyId', null)
  const taxCodeId = get(entity, 'taxCodeId', null)
  const supplierRef = get(entity, 'supplierRef', null)
  const label = getAccountingLabel(entity, isInvoice)

  if (posted) {
    return [{
      label: `Posted ${label}s cannot be changed`,
      type: DialogMessageType.WARNING,
    }]
  }

  if (postStatus === Posted_Invoice_Status.PENDING)
    return []

  if (type === InvoiceType.recurring || (!isInvoice && !canEditPurchaseInvoice(user, entity))) {
    return [{
      label: `To edit this ${label}, please log into your Clarity Pro / Enterprise system`,
      type: DialogMessageType.ERROR,
    }]
  }

  accountingErrors.push({
    isInvalid: !userHasPermission(user, permissions.EDIT_INVOICES),
    label: `You do not have permission to edit this ${label}`,
    type: DialogMessageType.ERROR,
  })

  accountingErrors.push({
    isInvalid: !userHasPermission(user, permissions.POST_INVOICES),
    label: `You do not have permission to post this ${label}`,
    type: DialogMessageType.ERROR,
  })

  accountingErrors.push({
    isInvalid: isNil(contactId),
    label: 'Contact is not specified or not valid',
    type: DialogMessageType.ERROR,
  })

  accountingErrors.push({
    isInvalid: isNil(companyId),
    label: 'Company is not specified or not valid',
    type: DialogMessageType.ERROR,
  })

  accountingErrors.push({
    isInvalid: isMissingAccountNumbers([entity], accountsIntegration),
    label: `${get(entity, 'company.name', null)} has no account number`,
    type: DialogMessageType.WARNING,
  })

  accountingErrors.push({
    isInvalid: isAccountNumberTooLarge(entity, accountsIntegration),
    label: `${get(entity, 'company.name', null)} has an account number which is too long.` +
      ` Only the first 8 characters will be used.`,
    type: DialogMessageType.WARNING,
  })

  accountingErrors.push({
    isInvalid: isEmpty(email) || !isEmailValid(email),
    label: 'Contact does not have a valid email address',
    type: DialogMessageType.ERROR,
  })

  accountingErrors.push({
    isInvalid: isNil(currencyId),
    label: `Currency is not specified in this ${label}`,
    type: DialogMessageType.ERROR,
  })

  accountingErrors.push({
    isInvalid: isNil(taxCodeId),
    label: `Tax Code is not specified in this ${label}`,
    type: DialogMessageType.ERROR,
  })

  if (!isInvoice && isSupplierRefMandatory(requiredFields)) {
    accountingErrors.push({
      isInvalid: isEmpty(supplierRef),
      label: `Supplier reference is not specified in this ${label}`,
      type: DialogMessageType.ERROR,
    })
  }

  accountingErrors.push({
    isInvalid: isEmpty(items),
    label: 'Please specify one or more line items',
    type: DialogMessageType.ERROR,
  })

  items.forEach(item => {
    const lineItemLetter = get(item, 'lineItemLetter', null)
    const description = get(item, 'desc', 'Item')
    const taxCodeId = get(item, 'taxCodeId', null)
    const nominalCode = get(item, 'nominalCode', null)

    accountingErrors.push({
      isInvalid: isNil(taxCodeId),
      label: `Tax code is not specified for line item ${lineItemLetter} - ${description}`,
      type: DialogMessageType.ERROR,
    })

    if (isAccountingPackageConnected) {
      accountingErrors.push({
        isInvalid: isEmpty(nominalCode),
        label: `Nominal code is not specified for line item ${lineItemLetter} - ${description}`,
        type: DialogMessageType.ERROR,
      })
    }
  })

  return accountingErrors.filter(accountingError => accountingError.isInvalid)
}

export const getPostToAccountPackageMessage = (accountsIntegration, accountingErrors) => {
  const accountPackageTitle = get(accountsIntegration, 'title', null)
  return isEmpty(accountingErrors)
    ? `Post this invoice to ${accountPackageTitle}?`
    : <ValidationErrorList validationErrors={accountingErrors}/>
}

export const filterRepostAccountingErrors = (accountingErrors) => {
  return accountingErrors.filter(error =>
    !error.label.includes('Posted') && !error.label.includes('cannot be changed')
  )
}

export const getRepostToAccountPackageMessage = (accountingErrors) => {
  return isEmpty(accountingErrors)
    ? 'This invoice has already been posted. Are you sure you want to repost this?'
    : <ValidationErrorList validationErrors={accountingErrors}/>
}

export const getStatus = (accountingInfo, accountsIntegration) => {

  if (isNil(accountingInfo.status))
    return 'Not Specified'

  if (accountsIntegration === accountingTypes.PRO_SAGE_50 && accountingInfo.status === '0')
    return 'Open'

  return accountingInfo.status
}

export const isMissingAccountNumbers = (invoices, accountsIntegration) => {
  if (accountsIntegration !== accountingTypes.SAGE_50)
    return false

  return invoices.some(isMissingAccountNumber)
}

export const isMissingAccountNumber = (invoice) => {
  const accountNumber = get(invoice, 'company.accountNumber', null)
  return isEmpty(accountNumber)
}

export const isAccountNumberTooLarge = (invoice, accountsIntegration) => {
  if (accountsIntegration !== accountingTypes.SAGE_50)
    return false

  const accountNumber = get(invoice, 'company.accountNumber', null)
  return !isEmpty(accountNumber) && accountNumber.length > 8
}

export const getUpdatedAccountNumbers = (accountNumbers, accountNumberField, value) => {
  const updatedAccountNumbers = {...accountNumbers}
  updatedAccountNumbers[accountNumberField.key] = value
  return updatedAccountNumbers
}

export const getAccountNumberField = (
  user,
  company,
  useCompanyNameForLabel,
  accountNumbers,
  isSkippingMissedCompanies) => {

  const accountsIntegration = getAccountsIntegrationForUser(user)
  const companyName = get(company, 'name')

  const label = useCompanyNameForLabel
    ? companyName
    : 'Account Number'

  const formFieldClassName = useCompanyNameForLabel
    ? `company-${parseCssSelector(companyName)}`
    : 'accountNumber'

  const value = accountNumbers[company.id] || ''

  const isSkipped = isSkippingMissedCompanies && isEmpty(value)

  const options = accountsIntegration === accountingTypes.SAGE_50
    ? {length: 8}
    : {}

  return {
    active: true,
    dataType: 'String',
    key: company.id,
    label,
    value,
    fieldKey: company.id,
    formFieldClassName,
    mandatory: !isSkipped,
    labelWidth: 300,
    labelMaxWidth: 290,
    options,
    placeholder: isSkipped ? 'Skipped' : undefined,
  }
}

export const getCompaniesFromInvoices = (invoices) => {
  const companies = invoices.map(invoice => invoice.company)
  return uniqBy(companies, company => company.id)
}

export const getAccountNumberFields = (user, invoices, accountNumbers, isSkippingMissedCompanies) => {

  const companies = getCompaniesFromInvoices(invoices)
  const companiesWithoutAccountNumber = getCompaniesWithoutAccountNumber(companies)
  const useCompanyNameForLabel = (companiesWithoutAccountNumber.length > 1)

  return companiesWithoutAccountNumber.map(company =>
    getAccountNumberField(user, company, useCompanyNameForLabel, accountNumbers, isSkippingMissedCompanies))
}

export const getAccountNumbersPromptMessage = (invoices) => {
  const companies = getCompaniesFromInvoices(invoices)
  const companiesWithoutAccountNumber = getCompaniesWithoutAccountNumber(companies)

  const singularMessage =
    "has no account number. Please specify one to post to an existing account or create a new one."
  const pluralMessage =
    "companies have no account number. Please specify them to post to an existing account or create a new one."

  return companiesWithoutAccountNumber.length === 1
    ? `${companiesWithoutAccountNumber[0].name} ${singularMessage}`
    : `${companiesWithoutAccountNumber.length} ${pluralMessage}`
}

export const getAccountNumbersPromptTitle = (invoices) => {
  const invoicesWithoutAccountNumber = getInvoicesWithoutAccountNumber(invoices)
  return invoicesWithoutAccountNumber.length === 1
    ? 'Specify Account Number'
    : 'Specify Account Numbers'
}

export const isAccountNumbersPromptCommitEnabled = (
  invoices,
  accountNumbers,
  isSkippingMissedCompanies
) => {
  if (isSkippingMissedCompanies)
    return true

  const companies = getCompaniesFromInvoices(invoices)
  const companiesWithoutAccountNumber = getCompaniesWithoutAccountNumber(companies)

  return companiesWithoutAccountNumber
    .map(company => getCompanyWithUpdatedAccountNumber(company, accountNumbers))
    .every(isCompanyHasAccountNumber)
}

export const getInvoicesWithoutAccountNumber = (invoices) => {
  return invoices.filter(invoice => !isCompanyHasAccountNumber(invoice.company))
}

export const getCompaniesWithoutAccountNumber = (companies) => {
  return companies.filter(company => !isCompanyHasAccountNumber(company))
}

export const isCompanyHasAccountNumber = (company) => {
  return !isEmpty(company.accountNumber)
}

export const getCompaniesWithUpdatedAccountNumbers = (invoices, accountNumbers) => {
  const companies = getCompaniesFromInvoices(invoices)
  return companies
    .map(company => isCompanyHasAccountNumber(company)
      ? company
      : getCompanyWithUpdatedAccountNumber(company, accountNumbers))
    .filter(isCompanyHasAccountNumber)
}

export const getCompanyWithUpdatedAccountNumber = (company, accountNumbers) => {
  return {
    ...company,
    accountNumber: accountNumbers[company.id],
  }
}

export const getInvoicesWithUpdatedAccountNumbers = (invoices, accountNumbers) => {
  const updatedCompanies = getCompaniesWithUpdatedAccountNumbers(invoices, accountNumbers)
  return invoices.filter(invoice => isInvoiceForCompany(invoice, updatedCompanies))
}

export const getInvoicesWithoutUpdatedAccountNumbers = (invoices, accountNumbers) => {
  const updatedCompanies = getCompaniesWithUpdatedAccountNumbers(invoices, accountNumbers)
  return invoices.filter(invoice => !isInvoiceForCompany(invoice, updatedCompanies))
}

export const isInvoiceForCompany = (invoice, companies) => {
  return companies.some(company => company.id === invoice.companyId)
}

export const isAccountNumbersPromptSkipOptionVisible = (invoices, accountNumbers) => {
  const invoicesWithoutAccountNumbers = getInvoicesWithoutAccountNumber(invoices)

  if (invoicesWithoutAccountNumbers.length <= 1)
    return false

  const skippedInvoices = getInvoicesWithoutUpdatedAccountNumbers(invoicesWithoutAccountNumbers, accountNumbers)
  if (isEmpty(skippedInvoices))
    return false

  const updatedCompanies =
    getCompaniesWithUpdatedAccountNumbers(invoicesWithoutAccountNumbers, accountNumbers)
  return !isEmpty(updatedCompanies)
}

export const getAccountNumbersPromptSkipOptionLabel = (invoices, accountNumbers) => {
  const invoicesWithoutAccountNumbers = getInvoicesWithoutAccountNumber(invoices)
  const skippedInvoices = getInvoicesWithoutUpdatedAccountNumbers(invoicesWithoutAccountNumbers, accountNumbers)
  return `Skip ${skippedInvoices.length} invoice(s) for remaining companies`
}
export const isValueValid = (value, values) => {
  if (isEmpty(value) || isNil(value)) {
    return true
  }
  return values.some(v => v.key === value)
}

export const markInvalid = (value, values) => {
  return isValueValid(value, values)
    ? value
    : value + ' Invalid'
}
